import { withReset, withStorageSync } from '@angular-architects/ngrx-toolkit';
import { computed, effect, inject } from '@angular/core';
import { withCallStatus } from '@ngrx-traits/signals';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  SeatCategoryCacheService,
  TicketCacheService,
} from '@seatix/check-in-data-access-offline-sync';
import { AuthApiService } from '@seatix/shared/data-access/auth';
import type { AssignedEventWithGates } from '@seatix/shared/domain';
import { catchError, EMPTY, firstValueFrom, pipe, switchMap, tap } from 'rxjs';
import { ScannerApiService } from '../scanner-api/scanner-api.service';

export interface InventoryMetadata {
  totalTickets: number;
  lastSync: number;
}

interface ScannerEventState {
  selectedEvent: AssignedEventWithGates | null;
  availableEvents: AssignedEventWithGates[];
  selectedGateId: number | null;
  overrideGateName: string | null;
  isLoading: boolean;
  error: string | null;
  inventoryDownloadStatus: 'idle' | 'loading' | 'success' | 'error';
  inventoryMetadata: Record<number, InventoryMetadata>;
}

const initialState: ScannerEventState = {
  selectedEvent: null,
  availableEvents: [],
  selectedGateId: null,
  overrideGateName: null,
  isLoading: false,
  error: null,
  inventoryDownloadStatus: 'idle',
  inventoryMetadata: {},
};

export const ScannerEventStore = signalStore(
  { providedIn: 'root' },
  withState<ScannerEventState>(initialState),
  withStorageSync({
    key: 'stxScannerEvent',
    autoSync: true,
    select: (state: ScannerEventState) => ({
      selectedEvent: state.selectedEvent,
      selectedGateId: state.selectedGateId,
      availableEvents: state.availableEvents,
      inventoryMetadata: state.inventoryMetadata,
    }),
  }),
  withReset(),
  withProps(() => ({
    _authApiService: inject(AuthApiService),
    _seatCategoryCacheService: inject(SeatCategoryCacheService),
    _ticketInventoryCacheService: inject(TicketCacheService),
    _scannerApiService: inject(ScannerApiService),
  })),
  withCallStatus({ collection: 'loadAssignedGates' }),
  withComputed((store) => {
    const selectedGate = computed(() => {
      const events = store.availableEvents();
      const gateId = store.selectedGateId();
      if (!gateId || events.length === 0) return null;

      for (const event of events) {
        const gate = event.assigned_gates.find((g) => g.id === gateId);
        if (gate) return gate;
      }
      return null;
    });

    const selectedEventId = computed(() => store.selectedEvent()?.id ?? null);

    const currentEventMetadata = computed(() => {
      const eventId = selectedEventId();
      if (!eventId) return null;
      return store.inventoryMetadata()[eventId] ?? null;
    });

    return {
      availableEventsLength: computed(() => store.availableEvents().length),
      hasSelectedEvent: computed(() => store.selectedEvent() !== null),
      hasSelectedGate: computed(() => selectedGate() !== null),
      selectedEventId,
      selectedEventName: computed(() => store.selectedEvent()?.name ?? ''),
      allEventGates: computed(() => {
        const event = store.selectedEvent();
        if (!event) return [];

        const gateMap = new Map<number, { id: number; name: string }>();
        for (const cat of event.seat_categories) {
          for (const gate of cat.gates ?? []) {
            const gateId = gate.id;
            if (gateId != null && !gateMap.has(gateId)) {
              gateMap.set(gateId, { id: gateId, name: gate.name });
            }
          }
        }
        return Array.from(gateMap.values()).sort((a, b) => a.id - b.id);
      }),
      selectedGateName: computed(() => {
        const override = store.overrideGateName();
        if (override) return override;
        const gate = selectedGate();
        return gate ? gate.name : '';
      }),
      currentEventMetadata,
      lastSyncTimestamp: computed(
        () => currentEventMetadata()?.lastSync ?? null,
      ),
    };
  }),
  withMethods((store) => {
    const authApiService = store._authApiService;
    const seatCategoryCacheService = store._seatCategoryCacheService;
    const ticketInventoryCacheService = store._ticketInventoryCacheService;
    const scannerApiService = store._scannerApiService;

    return {
      async selectEvent(event: AssignedEventWithGates | null): Promise<void> {
        patchState(store, { selectedEvent: event });

        if (event) {
          await seatCategoryCacheService
            .cacheSeatCategories(event.id, event.seat_categories)
            .catch((err) =>
              console.error('Failed to cache seat categories:', err),
            );
        }
      },
      async downloadInventory(from?: number): Promise<void> {
        const selectedEvent = store.selectedEvent();
        if (!selectedEvent) {
          console.warn('[Scanner] No event selected');
          return;
        }

        const eventId = selectedEvent.id;

        patchState(store, { inventoryDownloadStatus: 'loading' });

        try {
          const response = await firstValueFrom(
            scannerApiService.downloadInventory(eventId, from),
          );

          const { data } = response;

          if (data.status === 'new_data') {
            await ticketInventoryCacheService.cacheInventory(
              eventId,
              data.tickets,
            );

            patchState(store, {
              inventoryDownloadStatus: 'success',
              inventoryMetadata: {
                ...store.inventoryMetadata(),
                [eventId]: {
                  totalTickets: Object.keys(data.tickets).length,
                  lastSync: data.sync_to,
                },
              },
            });

            console.log(
              `[Scanner] Cached ${Object.keys(data.tickets).length} tickets for event ${eventId}, sync_to: ${data.sync_to}`,
            );
          } else {
            const currentEventMetadata = store.inventoryMetadata()[eventId];
            patchState(store, {
              inventoryDownloadStatus: 'success',
              inventoryMetadata: {
                ...store.inventoryMetadata(),
                [eventId]: {
                  totalTickets: currentEventMetadata?.totalTickets ?? 0,
                  lastSync: data.sync_to,
                },
              },
            });
            console.log(
              `[Scanner] Inventory already up to date, sync_to: ${data.sync_to}`,
            );
          }
        } catch (error) {
          console.error('[Scanner] Failed to download inventory', error);
          patchState(store, { inventoryDownloadStatus: 'error' });
        }
      },
      selectEventById(eventId: number): void {
        const event = store.availableEvents().find((e) => e.id === eventId);
        if (event) {
          patchState(store, {
            selectedEvent: event,
          });
        }
      },
      selectGate(gateId: number): void {
        patchState(store, { selectedGateId: gateId });
      },
      clearInventoryMetadata(): void {
        patchState(store, {
          inventoryMetadata: {},
          inventoryDownloadStatus: 'idle',
        });
      },
      loadAssignedGates: rxMethod<void>(
        pipe(
          tap(() => store.setLoadAssignedGatesLoading()),
          switchMap(() =>
            authApiService.getAssignedGates().pipe(
              tap((response) => {
                store.setLoadAssignedGatesLoaded();
                const availableEvents = response.data;
                patchState(store, {
                  availableEvents,
                });

                availableEvents.forEach((event) => {
                  seatCategoryCacheService
                    .cacheSeatCategories(event.id, event.seat_categories)
                    .catch((err) =>
                      console.error(
                        `Failed to cache seat categories for event ${event.id}:`,
                        err,
                      ),
                    );
                });
              }),
              catchError((err) => {
                store.setLoadAssignedGatesError(err);
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
    };
  }),
  withHooks((store) => ({
    onInit: () => {
      effect(() => {
        const selectedEvent = store.selectedEvent();
        if (selectedEvent) {
          const firstGate = selectedEvent.assigned_gates[0];
          if (firstGate) {
            store.selectGate(firstGate.id);
          }
        }
      });

      effect(() => {
        const availableEvents = store.availableEvents();
        const currentSelectedEvent = store.selectedEvent();

        if (availableEvents.length === 0) {
          return;
        }

        if (!currentSelectedEvent) {
          const firstEvent = availableEvents[0];
          store.selectEvent(firstEvent);
        }
      });
    },
  })),
);
